// library imports
import { Table, Typography, Flex, Empty, Skeleton, Button, theme } from "antd";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";
import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { useEffect, useRef, useState, useContext } from "react";
import dayjs from "dayjs";
// configs imports
import {
  DARK_BLUE,
  LIGHT_BLUE,
  LIGHT_LIGHT_BLUE,
  ORANGE,
  DARK_THEME_DARK_BLUE,
  DARK_GREEN,
  LIGHT_GREEN,
  PURPLE,
} from "../../configs/configs";
// hook imports
import useFetch from "../../hooks/useFetch";
// context imports
import { IndexContext } from "../../context/IndexContext";

const { Text } = Typography;

function MdChart({ container_name, data_line, data_bar, includePv }) {
  const { token } = theme.useToken();
  const { isLightTheme } = useContext(IndexContext);
  const container = uniqueId(container_name + "-");
  const chartRef = useRef(null);

  useEffect(() => {
    chartRef.current?.chart?.clear();

    const chartContainer = document.getElementById(container);
    chartContainer.innerHTML = "";

    const chart = new Chart({
      container: container,
      width: container.offsetWidth,
      height: container.offsetHeight,
      padding: "auto",
      autoFit: true,
    });

    const bar = chart
      .interval()
      .data(data_bar)
      .encode("x", "time")
      .encode("y", "power")
      .encode("color", "name")
      .scale("color", {
        domain: includePv
          ? [
              "PV (Self-Consumption)",
              "PV (Export)",
              "BESS Discharge",
              "BESS Charge",
              "TNB",
              "Load Demand",
            ]
          : ["BESS Discharge", "BESS Charge", "TNB", "Net Load Demand"],
        range: isLightTheme
          ? includePv
            ? [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_BLUE,
                ORANGE,
                PURPLE,
              ]
            : [LIGHT_BLUE, LIGHT_LIGHT_BLUE, DARK_BLUE, ORANGE]
          : includePv
            ? [
                DARK_GREEN,
                LIGHT_GREEN,
                LIGHT_BLUE,
                LIGHT_LIGHT_BLUE,
                DARK_THEME_DARK_BLUE,
                ORANGE,
                PURPLE,
              ]
            : [LIGHT_BLUE, LIGHT_LIGHT_BLUE, DARK_THEME_DARK_BLUE, ORANGE],
      })
      // Set opacity values conditionally based on the container
      .style("opacity", (d) => {
        if (container.startsWith("md_chart_before")) {
          if (d.name == "Net Load Demand" || d.name == "Load Demand") {
            return 1;
          } else {
            return 0.6;
          }
        } else {
          if (d.name == "TNB") {
            return 1;
          } else {
            return 0.6;
          }
        }
      })
      .transform({ type: "stackY" });

    chart.axis("x", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: "Time",
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });
    chart.axis("y", {
      line: true,
      lineStroke: token.colorTextBase,
      tickStroke: token.colorTextBase,
      title: "Power (kW)",
      titleFill: token.colorTextBase,
      labelFill: token.colorTextBase,
    });
    chart.legend({
      color: {
        itemLabelFill: token.colorTextBase,
      },
    });

    chart.interaction("tooltip", {
      render: (event, { title, items }) => {
        return `<div style="width:150px;">
        <h3 style="padding:0;margin:0">${title}</h3>
        <ul style="padding: 0; margin: 0; list-style: none;">
          ${items
            .map((d) => {
              // Format the value with commas and 2 decimal places
              const formattedValue = d.value
                ? Number(d.value).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : d.value === 0
                  ? Number(d.value).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : d.value;
              return `
                <li style="display: flex; justify-content: space-between; align-items: center; margin: 4px 0;">
                  <span style="display: inline-flex; align-items: center;">
                    <span style="width: 8px; height: 8px; background-color: ${d.color}; border-radius: 50%; margin-right: 8px;"></span>
                    <span>${d.name}</span>
                  </span>
                  <span>${formattedValue}</span>
                </li>`;
            })
            .join("")}
        </ul>
      </div>`;
      },
    });

    const line = chart
      .line()
      .data(data_line)
      .encode("x", "time")
      .encode("y", "power")
      .encode("color", "name")
      .encode("shape", "smooth")
      .style("stroke", ORANGE)
      // Set opacity values conditionally based on the container
      .style("opacity", () => {
        if (container.startsWith("md_chart_before")) {
          return 1;
        } else {
          return 0.6;
        }
      })
      .style("lineWidth", 3);

    document.getElementById(container).innerHTML = "";
    chartRef.current = chart;

    chart.render();

    chartRef.current = {
      chart,
      bar,
      line,
    };

    return () => {
      chartRef.current?.chart?.destroy();
    };
  }, [token, container_name]);

  useEffect(() => {
    chartRef.current?.bar?.changeData(data_bar);
    chartRef.current?.line?.changeData(data_line);
  }, [data_line, data_bar]);

  useEffect(() => {
    const chartContainer = document.getElementById(container);
    const resizeObserver = new ResizeObserver(() => {});

    resizeObserver.observe(chartContainer);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return <div style={{ width: "100%", height: "300px" }} id={container} />;
}

function MD({
  reportMode,
  searchParams,
  year,
  siteCommissioningDate,
  mdDateAfterBess,
  setMdDateAfterBess,
  startDateOri,
  endDateOri,
  setMdSummaryData,
  mdSummaryData,
  mdBreakdownData,
  setMdBreakdownData,
  totalSaving,
  setTotalSaving,
  totalMdKWSaving,
  setTotalMdKWSaving,
  mdDateBeforeBess,
  setMdDateBeforeBess,
  includePv,
  reportModeSite,
  loading,
  setLoading,
}) {
  const [fetchWrapper] = useFetch();
  const { siteSelected, notificationApi } = useContext(IndexContext);

  // startDate is the first day of the month based on left and right caret clicks
  const [startDate, setStartDate] = useState(
    dayjs().startOf("month").format("YYYY-MM-DD"),
  );

  const columns_summary = [
    {
      title: "",
      dataIndex: "Name",
    },
    {
      title: includePv ? "Before AIE-DR Reduction" : "Before BESS Reduction",
      dataIndex: "Before",
    },
    {
      title: includePv ? "After AIE-DR Reduction" : "After BESS Reduction",
      dataIndex: "After",
    },
  ];

  const [mdBarBefore, setMdBarBefore] = useState([]);
  const [mdBarAfter, setMdBarAfter] = useState([]);
  const [mdLineBefore, setMdLineBefore] = useState([]);
  const [mdLineAfter, setMdLineAfter] = useState([]);

  const columns_overall = [
    {
      title: "Date",
      dataIndex: "Date",
      width: "20%",
    },
    {
      title: includePv ? "Before AIE-DR Reduction" : "Before BESS Reduction",
      dataIndex: "Before",
      width: "40%",
    },
    {
      title: includePv ? "After AIE-DR Reduction" : "After BESS Reduction",
      dataIndex: "After",
      width: "40%",
    },
  ];

  /** API to get data related to MD */
  function getMdData() {
    setLoading(true);

    fetchWrapper({
      endpoint_url: "saving_report/getMdData",
      method: "GET",
      set_content_type_header: false,
      params: {
        site_id: siteSelected,
        start_date: startDate,
        include_pv: includePv,
      },

      onSuccess: (response) => {
        setMdSummaryData(response.data.md_summary_data);
        setMdBreakdownData(response.data.md_breakdown_data);
        setTotalSaving(response.data.total_saving);
        setTotalMdKWSaving(response.data.total_md_kw_saving);
        setMdDateBeforeBess(response.data.md_date_before_bess);
        setMdDateAfterBess(response.data.md_date_after_bess);
        setMdBarBefore(response.data.md_bar_before_bess);
        setMdBarAfter(response.data.md_bar_after_bess);
        setMdLineBefore(response.data.md_line_before_bess);
        setMdLineAfter(response.data.md_line_after_bess);
      },
      onResponseError: (response) => {
        notificationApi.error({
          message: "Failed to get MD data for saving report.",
          description: response.statusText,
        });
      },

      onFinish: () => {
        setLoading(false);
      },
    });
  }

  /** Function to get month and year from startDate so that the chart title can be updated */
  const getMdSavingTitle = () => {
    if (startDate) {
      // Create a new Date object from the startDate
      const dateObj = new Date(startDate);

      return dateObj.toLocaleString("en-US", {
        month: "long", // 'long' gives full month name (e.g., September)
        year: "numeric", // 'numeric' gives 4-digit year
      });
    } else {
      return "";
    }
  };

  /** Function to decrement the month when right caret is clicked */
  function decrementMonth() {
    // Create a Date object from startDate
    let dateObj = new Date(startDate);

    // Increment the month
    dateObj.setMonth(dateObj.getMonth() - 1);
    dateObj.setDate(1);

    // Format the new startDate back to 'YYYY-MM-DD'
    let newStartDate = dateObj.toISOString().split("T")[0]; // Splits to get 'YYYY-MM-DD'

    // Update the startDate to the new value
    setStartDate(newStartDate);
  }

  /** Function to increment the month when right caret is clicked */
  function incrementMonth() {
    // Create a Date object from startDate
    let dateObj = new Date(startDate);

    // Increment the month
    dateObj.setMonth(dateObj.getMonth() + 1);
    dateObj.setDate(1);

    // Format the new startDate back to 'YYYY-MM-DD'
    let newStartDate = dateObj.toISOString().split("T")[0]; // Splits to get 'YYYY-MM-DD'

    // Update the startDate to the new value
    setStartDate(newStartDate);
  }

  useEffect(() => {
    setMdBarBefore([]);
    setMdBarAfter([]);
    setMdLineBefore([]);
    setMdLineAfter([]);
    if (
      startDate &&
      siteSelected &&
      includePv !== null &&
      reportModeSite === siteSelected
    ) {
      getMdData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, siteSelected, includePv, reportModeSite]);

  useEffect(() => {
    setMdBarBefore([]);
    setMdBarAfter([]);
    setMdLineBefore([]);
    setMdLineAfter([]);
  }, [reportMode]);

  useEffect(() => {
    if (year && siteCommissioningDate) {
      if (year.year() < dayjs().year()) {
        setStartDate(
          dayjs(
            Math.max(
              year.startOf("year").valueOf(),
              dayjs(siteCommissioningDate, "YYYY-MM-DD").valueOf(),
            ),
          )
            .startOf("month")
            .format("YYYY-MM-DD"),
        );
      } else {
        setStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
      }
    }
  }, [year, siteCommissioningDate]);

  useEffect(() => {
    if (endDateOri) {
      setStartDate(dayjs(endDateOri).startOf("month").format("YYYY-MM-DD"));
    }
  }, [endDateOri]);

  return (
    <div>
      <Flex justify="space-between" align="center">
        <Button
          icon={
            parseInt(endDateOri.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ||
            parseInt(startDate.substring(5, 7)) === 1 ||
            parseInt(startDate.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ? (
              <></>
            ) : (
              <CaretLeftOutlined />
            )
          }
          type="text"
          disabled={loading}
          onClick={() => {
            decrementMonth();
          }}
        />

        <h1>
          {startDate < "2025-07-01"
            ? "Maximum Demand Saving "
            : "Capacity and Network Charges Savings "}{" "}
          {searchParams ? getMdSavingTitle() : ""}
        </h1>

        <Button
          icon={
            parseInt(endDateOri.substring(5, 7)) ===
              parseInt(startDateOri.substring(5, 7)) ||
            parseInt(startDate.substring(5, 7)) === 12 ||
            parseInt(startDate.substring(5, 7)) ===
              parseInt(endDateOri.substring(5, 7)) ? (
              <></>
            ) : (
              <CaretRightOutlined />
            )
          }
          type="text"
          disabled={loading}
          onClick={() => {
            incrementMonth();
          }}
        />
      </Flex>
      {startDate ? (
        !loading ? (
          <Flex justify="space-between" wrap={true} gap={8}>
            <div style={{ width: "calc(50vw - 55px)" }}>
              <Flex vertical={true}>
                <h2>Summary of Maximum Demand Data</h2>
                <Table
                  style={{ marginBottom: "12px", width: "100%" }}
                  size="small"
                  columns={columns_summary}
                  dataSource={mdSummaryData}
                  pagination={false}
                  bordered
                  summary={() => {
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            Maximum Demand Reduction
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} colSpan={2}>
                            <Text>{totalMdKWSaving}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>
                            Total Saving
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} colSpan={2}>
                            <Text>{totalSaving}</Text>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                />
                <h2>
                  Breakdown of Maximum Demand Data{" "}
                  {[5, 6, 8, 11].includes(siteSelected)
                    ? "during ToU Period"
                    : ""}
                </h2>
                <Table
                  size="small"
                  style={{ width: "100%" }}
                  bordered
                  columns={columns_overall}
                  dataSource={mdBreakdownData}
                  pagination={{
                    responsive: true,
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    position: "bottomRight",
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`,
                  }}
                />
              </Flex>
            </div>
            <div style={{ width: "calc(50vw - 55px)" }}>
              <Flex vertical={true}>
                <h2 style={{ margin: "0px" }}>
                  In the scenario where {includePv ? "AIE-DR" : "BESS"} is not
                  present
                </h2>
                <p style={{ margin: "0px", marginBottom: "4px" }}>
                  The Maximum Demand would be charged based on{" "}
                  {includePv ? "load demand" : "net load demand"}
                  {mdDateBeforeBess && mdDateBeforeBess !== "-"
                    ? ` of ${mdDateBeforeBess} profile`
                    : ""}
                </p>

                {mdBarBefore.length !== 0 || mdLineBefore.length !== 0 ? (
                  <div>
                    <MdChart
                      data_bar={mdBarBefore.map((item) => {
                        const newItem = { ...item };
                        for (const key in newItem) {
                          if (newItem[key] === "-") {
                            newItem[key] = null;
                          }
                        }
                        return newItem;
                      })}
                      data_line={mdLineBefore.map((item) => {
                        const newItem = { ...item };
                        for (const key in newItem) {
                          if (newItem[key] === "-") {
                            newItem[key] = null;
                          }
                        }
                        return newItem;
                      })}
                      container_name={"md_chart_before"}
                      includePv={includePv}
                    />
                  </div>
                ) : (
                  <div style={{ width: "100%", height: "100%" }}>
                    <Empty />
                  </div>
                )}
                <h2 style={{ margin: "0px" }}>
                  With {includePv ? "AIE-DR" : "BESS"} peak shaving
                </h2>
                <p style={{ margin: "0px", marginBottom: "4px" }}>
                  The Maximum Demand is charged based on TNB import
                  {mdDateAfterBess && mdDateAfterBess !== "-"
                    ? ` of ${mdDateAfterBess} profile`
                    : ""}
                </p>
                {mdBarAfter.length !== 0 || mdLineAfter.length !== 0 ? (
                  <div>
                    <MdChart
                      data_bar={mdBarAfter.map((item) => {
                        const newItem = { ...item };
                        for (const key in newItem) {
                          if (newItem[key] === "-") {
                            newItem[key] = null;
                          }
                        }
                        return newItem;
                      })}
                      data_line={mdLineAfter.map((item) => {
                        const newItem = { ...item };
                        for (const key in newItem) {
                          if (newItem[key] === "-") {
                            newItem[key] = null;
                          }
                        }
                        return newItem;
                      })}
                      container_name={"md_chart_after"}
                      includePv={includePv}
                    />
                  </div>
                ) : (
                  <div style={{ width: "100%", height: "100%" }}>
                    <Empty />
                  </div>
                )}
                <p style={{ fontSize: "10px" }}>
                  Disclaimer: Power demand calculation is estimated based on
                  metering data. Missing data may affect accuracy.
                </p>
              </Flex>
            </div>
          </Flex>
        ) : (
          <div style={{ height: "calc(100vh - 500px)" }}>
            <Skeleton active />
          </div>
        )
      ) : (
        <Empty />
      )}
    </div>
  );
}

export default MD;
